import { Loader2, Wifi, WifiOff } from 'lucide-react'
import { useJustReconnected, useOnlineStatus } from '../hooks/useOnlineStatus'

/**
 * Connectivity status pill, pinned to the top of the viewport.
 *
 * Mounted once at the app root rather than inside `Layout`, because the
 * workspace route renders outside `Layout` and losing your connection mid-
 * session is exactly when you most need to be told — a terminal that has
 * silently stopped responding is the worst version of this bug.
 *
 * Fixed rather than sticky so it can't be scrolled away, with safe-area padding
 * so it clears the notch in an installed PWA.
 */
export function OfflineBanner() {
  const { offline, checking, recheck } = useOnlineStatus()
  const justReconnected = useJustReconnected()

  if (!offline && !justReconnected) return null

  if (!offline) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 z-[60] flex justify-center px-4"
        style={{ top: 'calc(env(safe-area-inset-top) + 0.5rem)' }}
      >
        <div className="flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-1.5 text-sm font-medium text-emerald-700 shadow-sm backdrop-blur-sm dark:text-emerald-300">
          <Wifi className="h-4 w-4" aria-hidden="true" />
          Back online
        </div>
      </div>
    )
  }

  return (
    <div
      role="status"
      aria-live="assertive"
      className="fixed inset-x-0 z-[60] flex justify-center px-4"
      style={{ top: 'calc(env(safe-area-inset-top) + 0.5rem)' }}
    >
      <div className="flex max-w-full items-center gap-3 rounded-full border border-amber-500/30 bg-amber-500/95 px-4 py-1.5 text-sm font-medium text-amber-950 shadow-lg">
        <WifiOff className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span className="truncate">
          You&apos;re offline
          <span className="hidden sm:inline"> — changes can&apos;t be saved right now</span>
        </span>
        <button
          type="button"
          onClick={() => void recheck()}
          disabled={checking}
          className="flex-shrink-0 rounded-full bg-amber-950/15 px-2.5 py-0.5 text-xs font-semibold transition-colors hover:bg-amber-950/25 disabled:opacity-70"
        >
          {checking ? (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              Checking
            </span>
          ) : (
            'Retry'
          )}
        </button>
      </div>
    </div>
  )
}
